import { company } from '@/config/company';
import { env } from '@/config/env';

export type SeoProps = {
  /** Page title without the brand suffix. Omit on the home page. */
  title?: string;
  description: string;
  /** Site-relative path of this page, for the canonical URL. */
  path?: string;
  /** Absolute URL of the social preview image. */
  image?: string;
  /** Accessible description for a page-specific social preview image. */
  imageAlt?: string;
  /** Open Graph object type; `product` pairs with {@link SeoProps.product}. */
  ogType?: 'website' | 'article' | 'product';
  /** Price and availability for a shared product link's preview card. */
  product?: {
    brand: string;
    priceAmount: number | null;
    priceCurrency: string;
    availability: 'in stock' | 'preorder';
  };
  /** Pixel size of a page-specific `image`, so previews need not guess its shape. */
  imageSize?: { width: number; height: number };
  noindex?: boolean;
};

const DEFAULT_SOCIAL_IMAGE = '/social-preview.png';
const DEFAULT_SOCIAL_IMAGE_ALT =
  'Kabex Motors — cars sourced for you and delivered to your agreed destination.';

function absoluteUrl(value: string): string | null {
  try {
    return new URL(value, env.siteUrl ? `${env.siteUrl}/` : undefined).toString();
  } catch {
    return null;
  }
}

/**
 * Per-page head tags. React 19 hoists these into <head>, including during the
 * build-time prerender (§45).
 */
export function Seo({
  title,
  description,
  path,
  image,
  imageAlt,
  ogType = 'website',
  product,
  imageSize,
  noindex = false,
}: SeoProps) {
  const fullTitle = title
    ? `${title} | ${company.brandName}`
    : `${company.brandName} — ${company.motto}`;
  const canonical = env.siteUrl && path ? absoluteUrl(path) : null;
  const socialImage = absoluteUrl(image ?? DEFAULT_SOCIAL_IMAGE);
  const socialImageAlt = imageAlt ?? (image ? fullTitle : DEFAULT_SOCIAL_IMAGE_ALT);
  const xProfileUrl = company.socials.find((link) => link.platform === 'x')?.url;
  const xHandle = xProfileUrl?.match(/^https?:\/\/(?:www\.)?x\.com\/([^/?#]+)/i)?.[1];

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, follow" />}
      {canonical && <link rel="canonical" href={canonical} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={company.brandName} />
      <meta property="og:locale" content="en_UG" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {canonical && <meta property="og:url" content={canonical} />}
      {socialImage && (
        <>
          <meta property="og:image" content={socialImage} />
          <meta property="og:image:secure_url" content={socialImage} />
          {!image && <meta property="og:image:type" content="image/png" />}
          {!image && <meta property="og:image:width" content="1200" />}
          {!image && <meta property="og:image:height" content="630" />}
          {image && imageSize && (
            <>
              <meta property="og:image:width" content={String(imageSize.width)} />
              <meta property="og:image:height" content={String(imageSize.height)} />
            </>
          )}
          <meta property="og:image:alt" content={socialImageAlt} />
        </>
      )}
      {product && (
        <>
          <meta property="product:brand" content={product.brand} />
          <meta property="product:availability" content={product.availability} />
          <meta property="product:condition" content="new" />
          {product.priceAmount !== null && (
            <>
              <meta property="product:price:amount" content={String(product.priceAmount)} />
              <meta property="product:price:currency" content={product.priceCurrency} />
              <meta name="twitter:label1" content="Price" />
              <meta
                name="twitter:data1"
                content={`${product.priceCurrency} ${product.priceAmount.toLocaleString('en-US')}`}
              />
            </>
          )}
        </>
      )}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {xHandle && <meta name="twitter:site" content={`@${xHandle}`} />}
      {socialImage && <meta name="twitter:image" content={socialImage} />}
      {socialImage && <meta name="twitter:image:alt" content={socialImageAlt} />}
    </>
  );
}
